import { useContext, useState, useEffect, useRef } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { Input } from '../Common/Input';
import { validateZipCode, formatCanadianPostalCode } from '@/utils/zipCode';
import { useCalculateTaxByZipCode } from '@/api/tax';
import { shouldShowZipCodeFieldForTaxCalculation } from '@/utils/tax';
import CheckIcon from '@/assets/icons/common/check.svg';
import { useTranslate } from '@/hooks/useTranslate';

interface ZipCodeProps {
  onValidate?: (isValid: boolean) => void;
  isBlockedForZipCode?: boolean;
}

export const ZipCode = ({ onValidate, isBlockedForZipCode = false }: ZipCodeProps) => {
  const {
    actions: { updateAddressDetails, updatePriceBreakdown },
    addressDetails,
    customer,
    pricing,
  } = useContext(CheckoutContext);
  const [zipCodeValue, setZipCodeValue] = useState(addressDetails?.zipCode);
  const [error, setError] = useState('');
  const [isValidated, setIsValidated] = useState(pricing?.priceBreakdown?.isTaxValid);
  const isInitialValidationRef = useRef(true);
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onValidateRef = useRef(onValidate);
  const { mutateAsync: calculateTaxByZipCode } = useCalculateTaxByZipCode();
  const { t } = useTranslate();
  const isUSAOrCanada = shouldShowZipCodeFieldForTaxCalculation(customer);
  const countryCode = customer?.taxCountry ?? customer?.geoLocator?.countryCode2;
  const isCanada = countryCode === 'CA';
  const placeholderText = isCanada ? t('postalCode') : t('zip');
  const errorText = isCanada ? t('fieldErrors.invalidPostalCode') : t('fieldErrors.invalidZipCode');

  useEffect(() => {
    onValidateRef.current = onValidate;
  }, [onValidate]);

  useEffect(() => {
    onValidateRef.current?.(pricing?.priceBreakdown?.isTaxValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Skip API call on first render - validation state is already known from the pricing object
    if (isInitialValidationRef.current) {
      isInitialValidationRef.current = false;
      return;
    }

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    if (!zipCodeValue) {
      setError(isBlockedForZipCode ? errorText : '');
      setIsValidated(false);
      if (isUSAOrCanada) {
        onValidateRef.current?.(false);
      } else {
        onValidateRef.current?.(true);
      }
      return;
    }

    let formattedValue = zipCodeValue;
    if (isCanada) {
      formattedValue = formatCanadianPostalCode(zipCodeValue);
    }

    debounceTimeout.current = setTimeout(async () => {
      const isValid = validateZipCode(formattedValue, countryCode);
      if (!isValid) {
        if (isCanada && formattedValue.replace(/\s+/g, '').length < 3) {
          setError('');
        } else {
          setError(errorText);
        }
        setIsValidated(false);
        onValidateRef.current?.(false);
      } else {
        setError('');
        const valueToStore = isCanada ? formatCanadianPostalCode(formattedValue) : formattedValue;
        const { priceBreakdown, isValid, state } = await calculateTaxByZipCode({ zipCode: valueToStore });
        if (isValid) {
          updateAddressDetails({ zipCode: valueToStore, address: addressDetails?.address ?? '', state: state ?? '' });
          updatePriceBreakdown(priceBreakdown);
          setIsValidated(true);
          onValidateRef.current?.(true);
        } else {
          setError(errorText);
          setIsValidated(false);
          onValidateRef.current?.(false);
        }
      }
    }, 500);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipCodeValue, addressDetails?.address, isUSAOrCanada]);

  useEffect(() => {
    if (addressDetails?.zipCode.trim()) {
      setZipCodeValue(addressDetails?.zipCode);
    }
  }, [addressDetails?.zipCode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    // Format Canadian postal code as user types
    if (isCanada) {
      // Remove any spaces first, then format
      const cleaned = value.replace(/\s+/g, '');
      // Only allow alphanumeric characters and limit to 6 characters
      const alphanumeric = cleaned
        .replace(/[^A-Za-z0-9]/g, '')
        .slice(0, 6)
        .toUpperCase();

      // Format with space after 3 characters
      if (alphanumeric.length > 3) {
        value = alphanumeric.slice(0, 3) + ' ' + alphanumeric.slice(3);
      } else {
        value = alphanumeric;
      }
    }

    setZipCodeValue(value);
    setIsValidated(false);
    setError(''); // Clear error while typing
  };

  return (
    <Input
      type="text"
      placeholder={placeholderText}
      value={zipCodeValue ?? ''}
      onChange={handleChange}
      className="w-full"
      error={error}
      renderIcon={isValidated ? () => <img src={CheckIcon} className="w-5 h-5" /> : undefined}
    />
  );
};
